import { useState, useEffect } from "react";
import { DocusealBuilder } from "@docuseal/react";
import { fetchDocusealBuilderToken } from "../services/envelopes.service";

const FormBuilder = ({
  recipients = [],
  documentId,
  onComplete,
  mode = "send",
  previewMode = false,
}: {
  recipients?: any[],
  documentId: any,
  onComplete?: any,
  mode?: "send" | "configure",
  previewMode?: boolean,
}) => {
  const [token, setToken] = useState();

  useEffect(() => {
    const getToken = async () => {
      const resp: any = await fetchDocusealBuilderToken({
        email: recipients[0].email,
        documentId,
      });
      setToken(resp.token);
    };
    recipients.length && getToken();
  }, [documentId, recipients]);

  // Intercept fetch/XHR to detect when submissions API call succeeds (send mode only)
  useEffect(() => {
    if (!onComplete || mode !== "send") return;

    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      let url = '';
      if (typeof args[0] === 'string') {
        url = args[0];
      } else if (args[0] instanceof Request) {
        url = args[0].url;
      } else if (args[0] instanceof URL) {
        url = args[0].toString();
      }

      if (url.includes('/submissions') && response.ok) {
        const clonedResponse = response.clone();
        try {
          const data = await clonedResponse.json();

          const hasSentSubmitter = Array.isArray(data) &&
            data.some((item: any) =>
              Array.isArray(item.submitters) &&
              item.submitters.some((submitter: any) => submitter.status === 'sent')
            );

          if (hasSentSubmitter) {
            setTimeout(() => {
              onComplete({ status: 'sent', detectedVia: 'api-intercept', url, data });
            }, 1000);
          }
        } catch (e) {
          // Silent failure if parsing fails
        }
      }

      return response;
    };

    // Intercept XMLHttpRequest with proper typing
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method: string, url: string | URL, ...rest: any[]) {
      (this as any)._url = url.toString();
      return originalOpen.apply(this, [method, url, ...rest] as any);
    };

    XMLHttpRequest.prototype.send = function (...args: any[]) {
      this.addEventListener('load', function () {
        const customUrl = (this as any)._url;
        if (customUrl && customUrl.includes('/submissions') && this.status >= 200 && this.status < 300) {
          setTimeout(() => {
            onComplete({ status: 'sent', detectedVia: 'xhr-intercept', url: customUrl });
          }, 1000);
        }
      });
      return originalSend.apply(this, args as any);
    };

    return () => {
      window.fetch = originalFetch;
      XMLHttpRequest.prototype.open = originalOpen;
      XMLHttpRequest.prototype.send = originalSend;
    };
  }, [onComplete]);

  return (
    token && (
      <DocusealBuilder
        host="docuseal-bfdreuhpceeucuct.australiacentral-01.azurewebsites.net"
        token={token}
        submitters={recipients.map((e) => ({
          email: e.email,
          name: e.name,
          role: e.name,
        }))}
        withUploadButton={false}
        withDocumentsList={false}
        withSendButton={mode !== "configure" && !previewMode}
        withSignYourselfButton={mode !== "configure" && !previewMode}
        {...(mode === "configure" && !previewMode ? { autosave: true } : {})}
        {...(previewMode ? { previewMode: true } : {})}
        customCss={`
          /* Hide Edit icons in the recipient dropdown (Sidebar) */
          .lucide-pen, .lucide-pencil {
            display: none !important;
          }
          aside button:has(svg.lucide-pen) {
            display: none !important;
          }

          /* Disable Document Thumbnails Management (Replace, Menu, Add Document) */
          .lucide-more-vertical,
          .lucide-more-horizontal {
             display: none !important;
          }
          button[aria-haspopup="menu"],
          button[aria-haspopup="true"]:has(svg),
          button[aria-label="Options"],
          button[aria-label="Menu"],
          button[class*="menu-button"],
          button[class*="settings-button"] {
             display: none !important;
             pointer-events: none !important;
          }
          .tippy-box, [role="menu"] {
             display: none !important;
          }
        `}
      />
    )
  );
};

export default FormBuilder;
